import React from 'react';
import { Link } from 'react-router-dom';
import { Card, Button } from '../../components';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useScrollToTop } from '../../hooks/useScrollToTop';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

type PlanTier = {
  id: string;
  name: string;
  tagline: string;
  price?: string;
  priceNote?: string;
  approval?: boolean;
  features: string[];
  cta: string;
  ctaHref: string;
  featured?: boolean;
};

const PLANS: PlanTier[] = [
  {
    id: 'solo',
    name: 'Solo Founder',
    tagline: 'Build solo, learn in public.',
    price: '$10',
    priceNote: 'One-time',
    features: [
      'Full 6-phase journey',
      'Complete LMS access',
      'Group mentor sessions',
      'Phase & completion certificates',
      'Pitch to referred investors',
    ],
    cta: 'Register',
    ctaHref: '/register?plan=solo',
  },
  {
    id: 'team',
    name: 'Team',
    tagline: 'Your whole founding team, one account.',
    price: '$30',
    priceNote: 'One-time',
    features: [
      'Everything in Solo Founder',
      'Up to 4 team seats',
      'Shared startup profile',
      'Team-based progress tracking',
      'Cofounder workspace',
    ],
    cta: 'Register Team',
    ctaHref: '/register?plan=team',
    featured: true,
  },
  {
    id: 'partner',
    name: 'Partner',
    tagline: 'For universities & institutions.',
    approval: true,
    features: [
      'Read-only dashboards for your students',
      'Cohort analytics',
      'Phase progress per founder',
      'Export student data',
      'Custom onboarding',
    ],
    cta: 'Apply for Partnership',
    ctaHref: '/register?plan=partner',
  },
  {
    id: 'associate',
    name: 'Associate',
    tagline: 'For researchers, govt. & policy.',
    approval: true,
    features: [
      'Read-only anonymized cohort data',
      'Research data exports',
      'Quarterly insight reports',
      'Ecosystem quality metrics',
    ],
    cta: 'Request Access',
    ctaHref: '/register?plan=associate',
  },
  {
    id: 'investor',
    name: 'Investor',
    tagline: 'See curated startup pitches.',
    approval: true,
    features: [
      'Dashboard of referred pitches',
      'Request escalation & founder intros',
      'Schedule calls with founders',
      'Pitch deck access',
      'Portfolio tracker',
    ],
    cta: 'Apply as Investor',
    ctaHref: '/register?plan=investor',
  },
];

const CheckIcon = () => (
  <svg className="w-5 h-5 text-chart flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const BackArrow = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const WarnIcon = () => (
  <svg className="w-6 h-6 text-warning flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
  </svg>
);

const PlanCard: React.FC<{ plan: PlanTier }> = ({ plan }) => {
  const featured = plan.featured;
  return (
    <div
      className={[
        'relative flex flex-col h-full rounded-card p-6 md:p-7 transition-all duration-200',
        featured
          ? 'bg-surface border-2 border-chart shadow-lg shadow-chart/10'
          : 'bg-surface border border-line hover:border-chart/40 hover:shadow-md',
      ].join(' ')}
    >
      {featured && (
        <span className="absolute -top-3 right-6 bg-chart text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full shadow-md">
          Most Popular
        </span>
      )}

      <div className="mb-5">
        <h3 className="font-display text-3xl tracking-wide text-text uppercase mb-2">{plan.name}</h3>
        <p className="font-serif-display text-lg italic text-muted leading-snug">{plan.tagline}</p>
      </div>

      <div className="mb-6 pb-6 border-b border-line">
        {plan.price ? (
          <div className="flex items-baseline gap-2">
            <span className="font-display text-5xl text-text">{plan.price}</span>
            {plan.priceNote && <span className="text-sm text-muted">{plan.priceNote}</span>}
          </div>
        ) : (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-warning/10 text-warning text-sm font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-warning" />
            Requires Admin Approval
          </div>
        )}
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-3 text-text text-sm leading-relaxed">
            <CheckIcon />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <Link to={plan.ctaHref} className="block">
          <Button
            variant={featured ? 'primary' : 'ghost'}
            size="lg"
            className="w-full"
          >
            {plan.cta}
          </Button>
        </Link>
        {plan.approval && (
          <p className="mt-3 text-xs text-muted text-center flex items-center justify-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-warning" />
            Admin approval required before access
          </p>
        )}
      </div>
    </div>
  );
};

const Plans: React.FC = () => {
  useScrollToTop();
  const gridAnim = useScrollAnimation();
  const bannerAnim = useScrollAnimation();

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-bg text-text">
      <Header />

      <div className="section-gap pt-24 pb-12">
        {/* Back button */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted hover:text-text transition-colors"
        >
          <BackArrow />
          <span className="font-serif-display italic text-lg">Back</span>
        </Link>

        {/* Hero */}
        <div className="text-center py-8 md:py-12 animate-fade-in">
          <h1 className="font-display text-[3rem] md:text-[5rem] leading-none text-text uppercase tracking-wide mb-6">
            Choose Your Path
          </h1>
          <p className="font-serif-display text-xl md:text-3xl italic text-muted max-w-3xl mx-auto">
            Five ways to plug into Founder Lab — whether you're building, backing, studying, or partnering.
          </p>
        </div>

        {/* Admin approval banner */}
        <div
          ref={bannerAnim.ref}
          className={`animate-on-scroll ${bannerAnim.isVisible ? 'visible' : ''}`}
        >
          <Card className="border-l-4 border-warning bg-warning/10">
            <div className="flex items-start gap-4">
              <WarnIcon />
              <div>
                <h2 className="font-display text-xl tracking-wide text-text mb-2">
                  Some plans require admin approval
                </h2>
                <p className="font-serif-display text-lg italic text-muted leading-relaxed">
                  Partner, Associate, and Investor accounts can be created immediately but must be approved
                  by the Founder Lab team before access is granted. You'll receive an email once your
                  account is reviewed — typically within 1–3 business days.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Plans grid */}
        <div
          ref={gridAnim.ref}
          className={`animate-on-scroll ${gridAnim.isVisible ? 'visible' : ''}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {PLANS.map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </div>
        </div>

        {/* Footer CTA */}
        <Card>
          <div className="text-center py-4">
            <h2 className="font-display text-2xl md:text-3xl tracking-wide text-text mb-3">
              Not sure which plan fits?
            </h2>
            <p className="font-serif-display text-lg italic text-muted mb-6 max-w-2xl mx-auto">
              Reach out and we'll help you pick the right tier for your goals.
            </p>
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=ahmedbinaasim@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button variant="primary" size="lg">
                Contact Us
              </Button>
            </a>
          </div>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default Plans;
